import { useEffect, useRef } from 'react';

// A digit typed here is text, not a shortcut
const TYPES_TEXT = 'textarea, select, [contenteditable="true"], input:not([type="checkbox"]):not([type="range"])';

// The keys for the loop's start (a), its end (b), clearing it and going back to its start; the digits sit
// in the same spot on English and Spanish keyboards, numpad included
export const LOOP_KEYS = { a: '1', b: '2', clear: '3', restart: 'Enter' };
const ACTION_OF_KEY = { 1: 'a', 2: 'b', 3: 'clear', Enter: 'restart' };

/**
 * 1 marks the loop's start and 2 its end, each at the current time, 3 clears it and Enter goes back to its
 * start. A held key acts once, not on every repeat. `onRelease` hears the key come back up, or the window losing focus while it's down.
 * All are read through refs so the listeners are bound once; pass null `actions` while there's nothing to do.
 *
 * `restart` says whether there was a loop to go back to: without one, Enter is left to press a focused button.
 *
 * @param {{ mark: (edge: 'a' | 'b') => void, clear: () => void, restart: () => boolean } | null} actions
 */
const useLoopKeys = (actions, onRelease) => {
  const latest = useRef();
  latest.current = { actions, onRelease };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const action = ACTION_OF_KEY[e.key];
      if (!action || e.metaKey || e.ctrlKey || e.altKey) return;
      const { actions: act } = latest.current;
      if (e.target.closest?.(TYPES_TEXT) || !act) return;
      if (action === 'restart') {
        // A focused link still opens on Enter
        if (e.target.closest?.('a[href]') || (!e.repeat && !act.restart())) return;
      } else if (!e.repeat) {
        if (action === 'clear') act.clear();
        else act.mark(action);
      }
      // Also keeps a focused button from being pressed, and a held key from typing or scrolling
      e.preventDefault();
    };

    const handleKeyUp = (e) => {
      if (ACTION_OF_KEY[e.key]) latest.current.onRelease?.();
    };

    // Its keyup never comes if the window loses focus mid-press
    const handleBlur = () => latest.current.onRelease?.();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
};

export default useLoopKeys;
